import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Tooltip } from 'antd';
import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import LibraryPage from '../../../components/layout/LibraryPage';
import { TGDataTable, StatusTag } from '../../../components/common';
import PublishedDateFilterBar from './PublishedDateFilterBar';
import { useListTemplatesQuery } from '../../../store/api/templateApi';
import { formatDate } from '../../../utils/dateUtils';

export default function PublishedList({ embedded = false }) {
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState(null);

  const { data: response, isLoading } = useListTemplatesQuery({
    status: 'published',
  });

  const templates = response?.data || [];

  // Filter templates by published date
  const filteredTemplates = useMemo(() => {
    if (!dateFilter || !dateFilter.date) return templates;

    const { operator, date } = dateFilter;
    const filterTime = new Date(date).setHours(0, 0, 0, 0);

    return templates.filter((template) => {
      if (!template.updated_at) return false;
      const templateTime = new Date(template.updated_at).setHours(0, 0, 0, 0);

      switch (operator) {
        case 'on':
          return templateTime === filterTime;
        case 'after':
          return templateTime > filterTime;
        case 'before':
          return templateTime < filterTime;
        default:
          return true;
      }
    });
  }, [templates, dateFilter]);

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        sortable: true,
        filterable: true,
        filterType: 'text',
        width: 300,
        render: (name, record) => (
          <Space>
            <FileTextOutlined style={{ color: '#8B5CF6' }} />
            <span style={{ fontWeight: 500 }}>{name}</span>
          </Space>
        ),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        sortable: true,
        filterable: true,
        filterType: 'text',
        ellipsis: true,
        render: (desc) => desc || '—',
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
        sortable: true,
        filterable: true,
        filterType: 'select',
        filterOptions: [
          { label: 'RFP', value: 'rfp' },
          { label: 'RFI', value: 'rfi' },
          { label: 'RFQ', value: 'rfq' },
          { label: 'Survey', value: 'survey' },
          { label: 'Assessment', value: 'assessment' },
        ],
        render: (type) => (
          <StatusTag
            status={type}
            color={
              type === 'rfp'
                ? 'purple'
                : type === 'rfi'
                ? 'blue'
                : type === 'rfq'
                ? 'cyan'
                : 'green'
            }
          >
            {type?.toUpperCase() || 'TEMPLATE'}
          </StatusTag>
        ),
      },
      {
        title: 'Version',
        dataIndex: 'version',
        key: 'version',
        width: 100,
        sortable: true,
        filterable: true,
        filterType: 'number',
        render: (version) => `v${version || 1}`,
      },
      {
        title: 'Published',
        dataIndex: 'updated_at',
        key: 'updated_at',
        width: 180,
        sortable: true,
        render: (date) => formatDate(date),
      },
      {
        title: 'Created By',
        dataIndex: 'created_by_name',
        key: 'created_by',
        width: 150,
        sortable: true,
        filterable: true,
        filterType: 'text',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 160,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small">
            <Tooltip title="Complete this template">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/library/complete/${record.id}`)}
                style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
              >
                Complete
              </Button>
            </Tooltip>
          </Space>
        ),
      },
    ],
    [navigate]
  );

  const handleClearFilters = () => {
    setDateFilter(null);
  };

  const content = (
    <TGDataTable
      columns={columns}
      data={filteredTemplates}
      loading={isLoading}
      showExport
      showColumnConfig
      exportFilename="published-templates"
      presetKey="library.published"
      externalFilterCount={dateFilter && dateFilter.date ? 1 : 0}
      onClearExternalFilters={handleClearFilters}
      toolbar={
        <PublishedDateFilterBar
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
        />
      }
      rowKey="id"
      pagination={{
        pageSize: 20,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} published templates`,
      }}
      scroll={{ x: 1400 }}
    />
  );

  if (embedded) {
    return <div style={{ padding: '24px' }}>{content}</div>;
  }

  return (
    <LibraryPage
      title="Published Templates"
      breadcrumbs={[
        { label: 'Library', path: '/library' },
        { label: 'Published' },
      ]}
    >
      {content}
    </LibraryPage>
  );
}
